import { call } from '@advanza/api'
import { InputField, SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import { Button, LoadingDots } from '@advanza/ui'
import clsx from 'clsx'
import AutoRow from 'components/misc/AutoRow'
import { useToast } from 'components/misc/Toaster'
import React, { useEffect, useState } from 'react'

interface EmailTemplate {
    slug: string
    name: string
}

interface Provider {
    service_provider_id: number
    business_name: string
}

type EmailSide = 'supply' | 'demand'

interface SendTestMailRequest {
    templateSlug: string
    side: EmailSide
    providerId?: number
    requestId?: number
}

const EMAIL_SIDE_SUPPLY: EmailSide = 'supply'
const EMAIL_SIDE_DEMAND: EmailSide = 'demand'

const TestMailTemplate: React.FC = () => {
    const { addToast } = useToast()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [templates, setTemplates] = useState<EmailTemplate[]>([])
    const [selectedTemplate, setSelectedTemplate] = useState<string>('')
    const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null)
    const [entityId, setEntityId] = useState<string>('')
    const [side, setSide] = useState<EmailSide>(EMAIL_SIDE_SUPPLY)

    // Load available email templates
    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const response = await call('office/misc/get-email-templates', {
                    query: { side },
                })
                setTemplates(response.templates || [])
            } catch (error) {
                console.error('Failed to load templates:', error)
                addToast({ msg: 'Failed to load templates', red: true })
            }
        }
        loadTemplates()
    }, [side])

    const handleSendTestMail = async () => {
        if (!selectedTemplate || !entityId) {
            const entityLabel = side === EMAIL_SIDE_SUPPLY ? 'provider ID' : 'request ID'
            addToast({ msg: `Please select both template and ${entityLabel}`, red: true })
            return
        }

        if (isLoading) return

        setIsLoading(true)
        try {
            const requestData: SendTestMailRequest = {
                templateSlug: selectedTemplate,
                side,
            }

            if (side === EMAIL_SIDE_SUPPLY) {
                requestData.providerId = parseInt(entityId)
            } else {
                requestData.requestId = parseInt(entityId)
            }

            const response = await call('office/misc/send-test-mail', {
                json: requestData,
            })

            if (response.debugInfo) {
                const entityName =
                    side === EMAIL_SIDE_SUPPLY
                        ? response.debugInfo.providerName
                        : response.debugInfo.requestSubject || `Request #${entityId}`
                addToast({
                    msg: `Test mail sent successfully! Template: ${response.debugInfo.templateSlug}, ${side === EMAIL_SIDE_SUPPLY ? 'Provider' : 'Request'}: ${entityName}`,
                    green: true,
                    icon: 'rowing',
                })
            } else {
                addToast({ msg: 'Test mail sent successfully!', green: true, icon: 'rowing' })
            }
        } catch (error) {
            console.error('Failed to send test mail:', error)
            addToast({
                msg: `Failed to send test mail: ${
                    error instanceof Error ? error.message : 'Unknown error'
                }`,
                red: true,
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleEntityIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setEntityId(value)

        // Clear selected provider when ID changes (supply side only)
        if (
            side === EMAIL_SIDE_SUPPLY &&
            selectedProvider &&
            selectedProvider.service_provider_id.toString() !== value
        ) {
            setSelectedProvider(null)
        }
    }

    const handleSideChange = (newSide: EmailSide) => {
        setSide(newSide)
        setSelectedTemplate('')
        setEntityId('')
        setSelectedProvider(null) // Clear provider when switching sides
    }

    return (
        <div className="space-y-4">
            <div className="flex gap-4 items-center mb-4">
                <label className="text-md font-bold text-neutral-700">Email Side:</label>
                <div className="flex gap-2 border border-white rounded-md p-1 bg-neutral-50">
                    <button
                        type="button"
                        onClick={() => handleSideChange(EMAIL_SIDE_SUPPLY)}
                        className={clsx(
                            'px-4 py-2 rounded-md text-md font-medium transition-all',
                            side === EMAIL_SIDE_SUPPLY
                                ? 'bg-primary text-white shadow-md'
                                : 'text-grey-700 bg-white hover:bg-grey-200 border-solid border-[1px] border-grey-300',
                        )}>
                        Supply
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSideChange(EMAIL_SIDE_DEMAND)}
                        className={clsx(
                            'px-4 py-2 rounded-md text-md font-medium transition-all',
                            side === EMAIL_SIDE_DEMAND
                                ? 'bg-primary text-white shadow-md'
                                : 'text-grey-700 bg-white hover:bg-grey-200 border-solid border-[1px] border-grey-300',
                        )}>
                        Demand
                    </button>
                </div>
            </div>

            <AutoRow>
                <div className="min-w-[200px]">
                    <SelectSimple
                        value={selectedTemplate}
                        onChange={(e: SelectSimpleChangeEvent) =>
                            setSelectedTemplate(e.target.value as string)
                        }
                        options={[
                            { value: '', name: 'Select template...' },
                            ...templates.map((template) => ({
                                value: template.slug,
                                name: `${template.name} (${template.slug})`,
                            })),
                        ]}
                    />
                </div>

                <div className="min-w-[200px]">
                    <InputField
                        type="number"
                        value={entityId}
                        onChange={handleEntityIdChange}
                        placeholder={side === EMAIL_SIDE_SUPPLY ? 'Provider ID' : 'Request ID'}
                    />
                </div>

                <div className="flex items-end">
                    <Button
                        disabled={isLoading || !selectedTemplate || !entityId}
                        onClick={handleSendTestMail}>
                        {isLoading ? <LoadingDots /> : 'Send Test Mail'}
                    </Button>
                </div>
            </AutoRow>

            {entityId && (
                <div className="mt-3 p-3 bg-neutral-50 rounded-md">
                    <p className="text-md text-neutral-600">
                        <span className="font-semibold">
                            {side === EMAIL_SIDE_SUPPLY ? 'Provider ID' : 'Request ID'}:
                        </span>{' '}
                        {entityId}
                    </p>
                </div>
            )}

            <div className="mt-2 p-2 bg-blue-50 rounded-md">
                <p className="text-sm text-neutral-600">
                    Test mails will be sent to the configured test address
                </p>
            </div>
        </div>
    )
}

export default TestMailTemplate
